import { useEffect } from 'react';

import IterableEmbeddedSessionService from './IterableEmbeddedSessionService';

interface UseEmbeddedSessionOptions {
  /**
   * Whether embedded-message tracking is active for this user/app session.
   * Typically `isIterableInitialized && iterableCustomerAppEmbeddedMessagesEnabled`.
   * Flipping to `false` ends the active session (flushing it to Iterable) and
   * uninstalls the impression sink.
   */
  enabled: boolean;
}

/**
 * Drives the lifecycle of an Iterable embedded-message session.
 *
 * On mount (when `enabled`):
 *   - Installs the session service as the impression sink on
 *     `IterableEmbeddedService` so impression notifications from
 *     `useEmbeddedImpression` accrue into the session.
 *   - Calls `startSession()` so subsequent impressions are bound to it.
 *   - Listens for `visibilitychange`: tab hidden ends the session (flushing
 *     accrued impressions); tab visible starts a fresh one. This matches
 *     Iterable's guidance — sessions should not span periods the user can't
 *     actually see the placement.
 *   - Listens for `pagehide`: ends the session before the page goes away.
 *     `pagehide` is preferred over `beforeunload` because it also fires when
 *     the page enters bfcache and does not block the browser from caching.
 *
 * On unmount (or when `enabled` flips to `false`): ends the session and
 * removes listeners.
 *
 * Mount this once near the top of the app tree (alongside `useEmbeddedSync`)
 * — the embedded SDK keeps a single session at a time, so multiple instances
 * of this hook would race.
 */
export function useEmbeddedSession({ enabled }: UseEmbeddedSessionOptions): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const uninstall = IterableEmbeddedSessionService.install();
    IterableEmbeddedSessionService.startSession();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void IterableEmbeddedSessionService.endSession();
      } else {
        IterableEmbeddedSessionService.startSession();
      }
    };

    // `pagehide` flushes the session on real navigation away from the page.
    // The SDK's `endSession` issues a regular fetch (not `sendBeacon`), so
    // delivery on unload is best-effort; in practice the request is already
    // in flight before the page is torn down for typical SPA route changes
    // and tab closes.
    const handlePageHide = () => {
      void IterableEmbeddedSessionService.endSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      void IterableEmbeddedSessionService.endSession();
      uninstall();
    };
  }, [enabled]);
}
